/**
 * Product A+ Video — lazy load + tap-to-play
 */
(function () {
  'use strict';

  var ROOT = '[data-aplus-video]';
  var loaded = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function markLoaded(el) {
    if (loaded) loaded.add(el);
    else el.setAttribute('data-aplus-bound', '1');
  }

  function isLoaded(el) {
    if (loaded) return loaded.has(el);
    return el.getAttribute('data-aplus-bound') === '1';
  }

  function getSources(root) {
    try {
      return JSON.parse(root.getAttribute('data-sources') || '[]');
    } catch (e) {
      return [];
    }
  }

  function setPlaying(root, playing) {
    root.classList.toggle('is-playing', playing);
    root.classList.toggle('is-ready', true);
    var playBtn = root.querySelector('[data-aplus-play]');
    if (playBtn) {
      playBtn.setAttribute('aria-hidden', playing ? 'true' : 'false');
      playBtn.tabIndex = playing ? -1 : 0;
    }
  }

  function forceMute(video) {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.setAttribute('muted', '');
  }

  function ensureFileVideo(root) {
    var video = root.querySelector('[data-aplus-media]');
    if (!video || video.getAttribute('data-src-ready') === '1') return video;

    getSources(root).forEach(function (item) {
      if (!item || !item.src) return;
      var source = document.createElement('source');
      source.src = item.src;
      if (item.type) source.type = item.type;
      video.appendChild(source);
    });

    forceMute(video);
    video.setAttribute('data-src-ready', '1');
    video.preload = 'metadata';
    try {
      video.load();
    } catch (e) {}
    return video;
  }

  function playFile(root) {
    var video = ensureFileVideo(root);
    if (!video) return;

    forceMute(video);
    video.controls = true;
    video.setAttribute('controls', '');
    var p = video.play();
    if (p && typeof p.then === 'function') {
      p.then(function () {
        forceMute(video);
        setPlaying(root, true);
      }).catch(function () {
        setPlaying(root, false);
      });
    } else {
      setPlaying(root, true);
    }
  }

  function syncContentHeight(root) {
    var frame = root.querySelector('[data-aplus-frame]');
    var content = root.querySelector('.aplus-video__content');
    if (!frame || !content) return;

    if (!window.matchMedia('(min-width: 990px)').matches) {
      root.classList.remove('is-height-synced');
      root.style.removeProperty('--aplus-content-h');
      content.style.removeProperty('min-height');
      return;
    }

    var h = frame.offsetHeight;
    root.style.setProperty('--aplus-content-h', h + 'px');
    content.style.minHeight = h + 'px';
    root.classList.add('is-height-synced');
  }

  function observeHeight(root) {
    syncContentHeight(root);

    var frame = root.querySelector('[data-aplus-frame]');
    if (!frame || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', function () {
        syncContentHeight(root);
      });
      return;
    }

    var ro = new ResizeObserver(function () {
      syncContentHeight(root);
    });
    ro.observe(frame);

    window.addEventListener('resize', function () {
      syncContentHeight(root);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        syncContentHeight(root);
      });
    }
  }

  function bind(root) {
    if (isLoaded(root)) return;
    markLoaded(root);

    var playBtn = root.querySelector('[data-aplus-play]');
    var video = root.querySelector('[data-aplus-media]');

    if (playBtn) {
      playBtn.addEventListener('click', function (event) {
        event.preventDefault();
        playFile(root);
      });
    }

    if (video) {
      forceMute(video);
      video.addEventListener('volumechange', function () {
        if (!video.muted || video.volume > 0) forceMute(video);
      });
      video.addEventListener('play', function () {
        forceMute(video);
        setPlaying(root, true);
      });
      video.addEventListener('pause', function () {
        if (video.currentTime > 0.15) setPlaying(root, true);
      });
      video.addEventListener('ended', function () {
        setPlaying(root, false);
        video.controls = false;
        video.removeAttribute('controls');
      });
    }

    if ('IntersectionObserver' in window && video) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            ensureFileVideo(root);
            io.unobserve(root);
          });
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      );
      io.observe(root);
    }

    observeHeight(root);
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) init(event.target);
  });
})();
